import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { useAddTouchpointsFromMondayTemplateContext } from "components/modals/add-touchpoints-from-template-modal/context/add-touchpoints-from-monday-template-context";
import { getCampaignFolder } from "generated/api/campaign-folder";
import { getMonday } from "generated/api/monday";

import { useCampaign } from "hooks/api/use-campaigns";

import type { AddTouchpointsFromMondayTemplateFormData } from "components/modals/add-touchpoints-from-template-modal/add-touchpoints-from-template-modal.types";

/**
 * Hook to auto-fetch and select the campaign template associated with a folder.
 * When a folderId is provided, this hook:
 * 1. Fetches the folder details to check whether it is linked to Monday
 * 2. Calls the backend endpoint to get the folder's campaign template
 * 3. Pre-populates the form with the template
 */
export const useFolderCampaignTemplate = () => {
  const { clientId, campaignId } = useParams<{ clientId: string; campaignId: string }>();
  const { data: currentCampaign } = useCampaign({ clientId, id: campaignId });
  const {
    folderId,
    isDirectToStep2,
    setCampaignTemplates,
    setIsLoadingCampaignTemplates,
    setExistingTouchpointNames,
  } = useAddTouchpointsFromMondayTemplateContext();
  const { setValue } = useFormContext<AddTouchpointsFromMondayTemplateFormData>();
  const transferStudentStatusFlag = currentCampaign?.transferStudentStatusFlag ?? false;

  useEffect(() => {
    if (!isDirectToStep2 || !folderId || !clientId || !campaignId || !currentCampaign) {
      return;
    }

    const fetchCampaignTemplateForFolder = async () => {
      setIsLoadingCampaignTemplates(true);
      try {
        const folderApi = getCampaignFolder();
        const folderDetails = await folderApi.getOneFolder({
          clientId,
          campaignId,
          id: folderId,
        });

        if (!folderDetails.isMondayFolder) {
          console.warn("Folder is not linked to Monday:", folderId);
          return;
        }

        // Step 2: Get the campaign template and existing touchpoint names in parallel
        // (monday requests take long so trying to batch when possible)
        const mondayApi = getMonday();
        const [template, existingSubitemNames] = await Promise.all([
          mondayApi.getFolderTemplate({ folderId }, { transferStudentStatusFlag }),
          mondayApi.getFolderSubitemNames({ folderId }),
        ]);

        setExistingTouchpointNames(new Set(existingSubitemNames));

        setCampaignTemplates([template]);
        setValue("campaignTemplates", [template.id || ""]);

      } catch (error) {
        console.error("Failed to fetch campaign template for folder:", error);
        setCampaignTemplates([]);
      } finally {
        setIsLoadingCampaignTemplates(false);
      }
    };

    fetchCampaignTemplateForFolder();
  }, [
    isDirectToStep2,
    folderId,
    clientId,
    campaignId,
    currentCampaign,
    transferStudentStatusFlag,
    setCampaignTemplates,
    setIsLoadingCampaignTemplates,
    setExistingTouchpointNames,
    setValue,
  ]);
};
